import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { api, inr } from '../lib/api';
import { ShieldCheck, CheckCircle2, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EnquiryModal — "Get Best Price" form used by Product, Vendor, and other CTAs.
 *
 * Authentication contract:
 *  - Logged-in customers: name/mobile/email come from /api/auth/me. No OTP step.
 *  - Anonymous: two-step flow → (1) collect lead details + send OTP, (2) verify
 *    OTP and create the enquiry in a single POST to /api/enquiries.
 *
 * Targeting contract:
 *  - `product` set → product_id wins; backend resolves the supplier from the
 *    product doc.
 *  - `supplier` set & no product → supplier_id is sent directly (used on the
 *    vendor profile page's "Get Best Price" CTA).
 */
const EnquiryModal = ({ open, onOpenChange, product, supplier }) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  // 'form' = lead details, 'otp' = anonymous OTP gate. Logged-in users skip 'otp'.
  const [step, setStep] = useState('form');
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [form, setForm] = useState({
    name: '', mobile: '', email: '', quantity: '', message: '',
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.me().then((u) => { if (!cancelled) setUser(u || null); }).catch(() => { if (!cancelled) setUser(null); });
    // Reset the OTP step + cached devOtp every time the modal re-opens so a
    // previous attempt never bleeds into a new one.
    setStep('form'); setOtp(''); setDevOtp(null);
    return () => { cancelled = true; };
  }, [open]);

  const buildPayload = () => {
    const base = user
      ? { name: user.name || '', mobile: user.mobile || '', email: user.email || '' }
      : { name: form.name, mobile: form.mobile, email: form.email };
    return {
      ...base,
      quantity: form.quantity,
      message: form.message,
      product_id: product?.id,
      product_name: product?.name,
      // When the enquiry is filed straight from a vendor profile (no product
      // context) we route the lead directly to that supplier.
      supplier_id: !product?.id ? (supplier?.id || undefined) : undefined,
    };
  };

  // Step 1 for anonymous users: validate the form and dispatch the OTP.
  const requestOtp = async () => {
    if (!form.name || !form.mobile) {
      toast({ title: 'Please fill name and mobile', variant: 'destructive' });
      return;
    }
    const mobileClean = (form.mobile || '').replace(/\D/g, '').slice(-10);
    if (mobileClean.length !== 10) {
      toast({ title: 'Enter a valid 10-digit mobile number', variant: 'destructive' });
      return;
    }
    try {
      setOtpSending(true);
      const r = await axios.post(`${API}/auth/send-otp`, { mobile: mobileClean });
      setDevOtp(r.data?.dev_otp || null);
      setStep('otp');
      toast({ title: 'OTP sent', description: `Sent to +91-${mobileClean}` });
    } catch (err) {
      toast({ title: 'Failed to send OTP', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setOtpSending(false);
    }
  };

  // Final submit. For logged-in users this is the only step. For anonymous
  // users this is step 2 (after OTP entry).
  const submit = async () => {
    const payload = buildPayload();
    // Mobile is the only truly required field — for logged-in users it always
    // comes from their (already OTP-verified) account; for anonymous it was
    // collected in step 1 and re-verified via OTP. Name is optional for
    // logged-in users because OTP-only signups leave it blank; we fall back
    // to a friendly placeholder rather than blocking the lead.
    if (!payload.mobile) {
      toast({ title: 'Mobile number is required', variant: 'destructive' });
      return;
    }
    if (!user && !payload.name) {
      toast({ title: 'Please enter your name', variant: 'destructive' });
      return;
    }
    if (user && !payload.name) {
      payload.name = `Buyer ${(payload.mobile || '').slice(-4)}`;
    }
    if (!user) {
      if (!otp || otp.length < 4) {
        toast({ title: 'Enter the OTP', variant: 'destructive' });
        return;
      }
      payload.otp = otp.trim();
    }
    try {
      setSubmitting(true);
      await api.createEnquiry(payload);
      toast({ title: 'Enquiry sent successfully', description: 'The supplier will contact you within 24 hours.' });
      onOpenChange(false);
      setForm({ name: '', mobile: '', email: '', quantity: '', message: '' });
      setOtp(''); setStep('form'); setDevOtp(null);
    } catch (err) {
      toast({ title: 'Failed to send enquiry', description: err?.response?.data?.detail || 'Try again later.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const onPrimary = (e) => {
    e.preventDefault();
    if (user) return submit();           // logged-in → one click
    if (step === 'form') return requestOtp();
    return submit();                      // step === 'otp'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="enquiry-modal">
        <DialogHeader>
          <DialogTitle className="text-[#2c54b2]">Get Best Price</DialogTitle>
          <DialogDescription>
            {step === 'otp'
              ? 'Verify your mobile number to send this enquiry.'
              : 'Share your requirement and get the best quotes from verified sellers.'}
          </DialogDescription>
        </DialogHeader>

        {product && (
          <div className="flex gap-3 p-3 bg-gray-50 rounded-md" data-testid="enquiry-product-context">
            <img src={product.image} alt={product.name} className="w-16 h-16 rounded object-cover" />
            <div className="min-w-0">
              <p className="text-sm font-medium line-clamp-2">{product.name}</p>
              <p className="text-sm font-bold text-[#2c54b2] mt-1">{inr(product.price)} / {product.unit}</p>
            </div>
          </div>
        )}
        {!product && supplier && (
          <div className="flex gap-3 p-3 bg-gray-50 rounded-md" data-testid="enquiry-supplier-context">
            <div className="w-12 h-12 rounded bg-[#2c54b2] text-white flex items-center justify-center font-semibold">{(supplier.name || '?').slice(0, 2).toUpperCase()}</div>
            <div className="min-w-0">
              <p className="text-sm font-medium line-clamp-2">{supplier.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{supplier.city || ''}</p>
            </div>
          </div>
        )}

        <form onSubmit={onPrimary} className="space-y-3">
          {step === 'form' && user && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-xs space-y-1" data-testid="enquiry-logged-in-summary">
              <div className="flex items-center gap-1.5 font-medium text-green-800">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Sending as {user.name || `Buyer ${(user.mobile || '').slice(-4)}`}
              </div>
              <div className="text-green-900/80 grid grid-cols-[60px_1fr] gap-x-2">
                <span className="text-gray-500">Mobile</span><span data-testid="enquiry-prefill-mobile">{user.mobile || '—'}</span>
                {user.email ? (<><span className="text-gray-500">Email</span><span className="truncate" data-testid="enquiry-prefill-email">{user.email}</span></>) : null}
              </div>
              {!user.name && (
                <p className="text-[11px] text-amber-700 pt-1">
                  Tip: <a href="/account" className="underline">add your name in profile</a> so suppliers know who they&apos;re quoting.
                </p>
              )}
              <p className="text-[11px] text-gray-500 pt-1">These details are taken from your profile and shared only with the seller.</p>
            </div>
          )}

          {step === 'form' && !user && (
            <>
              <div>
                <Label htmlFor="name" className="text-xs">Name *</Label>
                <Input id="name" data-testid="enquiry-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" required />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="mobile" className="text-xs">Mobile *</Label>
                  <Input id="mobile" data-testid="enquiry-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="10-digit mobile" required />
                </div>
                <div>
                  <Label htmlFor="qty-anon" className="text-xs">Quantity</Label>
                  <Input id="qty-anon" data-testid="enquiry-quantity-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder={product?.moq || 'e.g., 100 Pcs'} />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className="text-xs">Email</Label>
                <Input id="email" data-testid="enquiry-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
              </div>
            </>
          )}

          {step === 'form' && user && (
            <div>
              <Label htmlFor="qty" className="text-xs">Quantity</Label>
              <Input id="qty" data-testid="enquiry-quantity-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder={product?.moq || 'e.g., 100 Pcs'} />
            </div>
          )}

          {step === 'form' && (
            <div>
              <Label htmlFor="msg" className="text-xs">Message</Label>
              <Textarea id="msg" data-testid="enquiry-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Describe your requirement, expected delivery, etc." rows={3} />
            </div>
          )}

          {step === 'otp' && (
            <div className="space-y-3" data-testid="enquiry-otp-step">
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                <div className="flex items-center gap-1.5 font-medium">
                  <KeyRound className="w-3.5 h-3.5" />
                  OTP sent to +91-{(form.mobile || '').replace(/\D/g, '').slice(-10)}
                </div>
                <p className="mt-1 text-[11px]">Enter the 6-digit code to confirm and send your enquiry.</p>
                {devOtp && (
                  <p className="mt-1 text-[11px] font-mono bg-white border border-blue-200 rounded px-2 py-1 inline-block">DEV OTP: {devOtp}</p>
                )}
              </div>
              <div>
                <Label htmlFor="otp" className="text-xs">OTP</Label>
                <Input id="otp" data-testid="enquiry-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit code" inputMode="numeric" autoFocus />
              </div>
              <button type="button" onClick={requestOtp} disabled={otpSending} className="text-xs text-[#2c54b2] hover:underline" data-testid="enquiry-otp-resend">
                {otpSending ? 'Resending…' : 'Resend OTP'}
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-gray-600 bg-blue-50 p-2 rounded">
            <ShieldCheck className="w-4 h-4 text-green-600" />
            <span>Your contact details are safe. We share only with verified suppliers.</span>
          </div>

          <div className="flex gap-2">
            {step === 'otp' && (
              <Button type="button" variant="outline" onClick={() => { setStep('form'); setOtp(''); }} data-testid="enquiry-otp-back">Back</Button>
            )}
            <Button type="submit" data-testid="enquiry-submit-button" disabled={submitting || otpSending} className="flex-1 bg-[#ee5e23] hover:bg-[#d24e15] text-white">
              {submitting ? 'Sending…' : (user || step === 'otp' ? 'Send Enquiry' : 'Continue')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryModal;
